import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationItem } from 'src/app/shared/components/navigation/navigation.model';
import { NavigationService } from 'src/app/core/navigation/navigation.service';
import { ContextSelectorService } from 'src/app/core/services/context-selector.service';
import { UserService } from 'src/app/core/user/user.service';
import { TenantService } from 'src/app/modules/administration/tenant/tenant.service';
import { BusinessUnitService } from 'src/app/modules/master-data/business-unit/business-unit.service';
import { FacilityService } from 'src/app/modules/master-data/facility/facility.service';
import { ITenant } from 'src/app/modules/administration/tenant/tenant';
import { IBusinessUnit } from 'src/app/modules/master-data/business-unit/business-unit';
import { TranslateModule } from '@ngx-translate/core';
import { IFacility } from 'src/app/modules/master-data/facility/facility';
import { ModuleRailComponent } from '../../common/module-rail/module-rail.component';
import { ContextSidebarComponent } from '../../common/context-sidebar/context-sidebar.component';
import { TopNavbarComponent } from '../../common/top-navbar/top-navbar.component';
import {
  ContextSelectorPanelComponent,
  WorkingContextApplyEvent,
} from '../../common/context-selector-panel/context-selector-panel.component';
import { MobileViewPromptService } from '../../common/mobile-view-prompt/mobile-view-prompt.service';

/**
 * Shell desktop (rail + context sidebar + topbar + outlet).
 *
 * KHÔNG có nhánh responsive xuống cỡ điện thoại (gỡ 2026-08-07): bottom-nav 5 tab,
 * mobile-drawer và hamburger đã bị xoá — chúng render đè lên mọi màn desktop ở cửa sổ
 * hẹp (ngưỡng cũ là alias `md` = 960px của `appConfig.screens`, tức là cả trên laptop).
 * Giao diện điện thoại là shell riêng ở `/mobile` (epic MOBILE-01); dưới 768px shell này
 * chỉ mời chuyển qua đó bằng `MobileViewPromptService` và giữ nguyên bố cục (min-width).
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'admin-layout',
  imports: [
    RouterOutlet,
    ModuleRailComponent,
    ContextSidebarComponent,
    TopNavbarComponent,
    ContextSelectorPanelComponent,
    TranslateModule,
  ],
  templateUrl: './admin.component.html',
  // Chiều rộng tối thiểu thay cho nhánh responsive: dưới ngưỡng này trang CUỘN NGANG
  // (`layout.component.html` để `overflow-x-auto`) chứ không reflow. Đặt ở CSS chứ không
  // phải class `min-w-[1024px]` để không thêm nợ cho luật adr/no-hardcoded-tw-pixel.
  styles: [':host .desktop-shell { min-width: 1024px; }'],
})
export class AdminLayoutComponent implements OnInit {
  private _navigationService = inject(NavigationService);
  private _mobileViewPrompt = inject(MobileViewPromptService);
  private _router = inject(Router);
  private _cdr = inject(ChangeDetectorRef);
  public contextSelectorService = inject(ContextSelectorService);
  private _tenantService = inject(TenantService);
  private _businessUnitService = inject(BusinessUnitService);
  private _facilityService = inject(FacilityService);
  private _userService = inject(UserService);
  private _destroyRef = inject(DestroyRef);

  navigation!: NavigationItem[];
  contextSidebarCollapsed = false;

  // Context selector panel state
  contextSelectorOpen = false;
  tenants: ITenant[] = [];
  businessUnits: IBusinessUnit[] = [];
  facilities: IFacility[] = [];
  selectedTenant: ITenant | null | undefined = null;
  selectedBusinessUnit: IBusinessUnit | null | undefined = null;

  selectedFacility: IFacility | null | undefined = null;

  // G3: Org claim filtering state
  allowedBusinessUnitIds: string[] = [];
  allowedFacilityIds: string[] = [];
  isAdmin = false;

  ngOnInit(): void {
    // Subscribe to navigation data (single source of truth in NavigationService)
    this._navigationService.navigation$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((navigation: NavigationItem[]) => {
        this.navigation = navigation;
        this._cdr.markForCheck();
      });

    // Viewport < 768px → mời chuyển sang giao diện mobile (`/mobile`). Đặt ở shell nên
    // mọi màn desktop đều có, không phải nhét vào từng trang.
    this._mobileViewPrompt.start();
    this._destroyRef.onDestroy(() => this._mobileViewPrompt.stop());

    // Subscribe to context selector panel open/close
    this.contextSelectorService.isOpen$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isOpen) => {
        this.contextSelectorOpen = isOpen;
        this._cdr.markForCheck();
      });

    // Subscribe to tenant/BU data for the context selector panel
    combineLatest([
      this._tenantService.tenants$,
      this._tenantService.selectedTenant$,
      this._businessUnitService.businessUnits$,
      this._businessUnitService.selectedBusinessUnit$,
      this._facilityService.facilities$,
      this._facilityService.selectedFacility$,
      this._userService.allowedBusinessUnitIds$,
      this._userService.allowedFacilityIds$,
      this._userService.isAdmin$,
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        ([
          tenants,
          selectedTenant,
          businessUnits,
          selectedBusinessUnit,
          facilities,
          selectedFacility,
          allowedBUs,
          allowedFacilities,
          isAdmin,
        ]) => {
          this.tenants = tenants;
          this.selectedTenant = selectedTenant;
          this.businessUnits = businessUnits;
          this.selectedBusinessUnit = selectedBusinessUnit;
          this.facilities = facilities;
          this.selectedFacility = selectedFacility;
          // G3: Wire org claim filtering data
          this.allowedBusinessUnitIds = allowedBUs;
          this.allowedFacilityIds = allowedFacilities;
          this.isAdmin = isAdmin;
          this._cdr.markForCheck();
        },
      );
  }

  /** Handle context apply from the panel */
  onApplyContext(event: WorkingContextApplyEvent): void {
    // Nhớ lựa chọn (qua localStorage): URL mang giá trị ĐƠN (25 màn đọc `businessUnitId` như một
    // Guid), lựa chọn NHIỀU đi qua header do orgContextInterceptor đọc từ đây. Panel rơi về hợp
    // đồng đơn (danh mục bối cảnh 404) thì hai danh sách header là null.
    this.contextSelectorService.setLastAppliedSelection({
      tenantId: event.tenantId,
      businessUnitIds: event.businessUnitIds,
      facilityIds: event.facilityIds,
      businessUnitId: event.businessUnitId,
      facilityId: event.facilityId,
      headerBusinessUnitIds: event.headerBusinessUnitIds,
      headerFacilityIds: event.headerFacilityIds,
    });
    // Ghi nhớ "Tất cả đơn vị" là lựa chọn có chủ ý. Không có cờ này thì lần tải sau nó không
    // phân biệt được với "chưa chọn lần nào" và layout tự gán đơn vị đầu tiên.
    this.contextSelectorService.setAllBusinessUnitsChosen(
      event.businessUnitId == null && !event.headerBusinessUnitIds?.length,
    );
    // Như trên, cho "Tất cả cơ sở" — layout chỉ đọc cờ này với tài khoản bị giới hạn theo cơ sở.
    this.contextSelectorService.setAllFacilitiesChosen(
      event.facilityId == null && !event.headerFacilityIds?.length,
    );

    this._router.navigate([], {
      queryParams: {
        tenantId: event.tenantId,
        businessUnitId: event.businessUnitId,
        facilityId: event.facilityId,
      },
      queryParamsHandling: 'merge',
    });
    this.contextSelectorService.close();
  }
}
